import React, { useState, useEffect } from 'react';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ActionNav } from '@uhg-abyss/web/ui/ActionNav';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { Text } from '@uhg-abyss/web/ui/Text';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Skeleton } from '@uhg-abyss/web/ui/Skeleton';
import { useDSOrganizationsQuery } from '../../../../common/hooks/useDSOrganizations';
import { event } from '@uhg-abyss/web/tools/event';

const StyledActionNavItem = styled(ActionNav.Item, {
  '&.abyss-action-nav-link-wrapper': {
    minWidth: '0px',
    fontSize: 'small',
  },
  '&.abyss-action-nav-link-container': {
    '& svg': {
      width: '20px',
    },
  },
  '&.abyss-action-nav-icon': {
    display: 'none',
  },
});

export const AccessRequestsBarWidget = () => {
  const router = useRouter();
  const { data } = usePropsContext();
  const {
    userRequestsResult,
    thirdPartyRequestsResult,
    userRequestsResultLoading,
    thirdPartyRequestsResultLoading,
    user,
  } = data;

  const { paa, ia } = user;
  const [dsOrganization, setDSOrganization] = useState('');
  const { uhcProviderId } = dsOrganization || '';
  const [isAccessBarLoading, setIsAccessBarLoading] = useState(true);
  const [filteredAccessBarItems, setFilteredAccessBarItems] = useState([]);

  const getCount = (requests) => {
    if (requests) {
      const clonedRequests = [...requests];
      if (!uhcProviderId) {
        return clonedRequests.reduce((count, val) => {
          const users = val?.users || [];
          return count + users.length;
        }, 0);
      }
      return clonedRequests
        .filter((r) => r.uhcProviderId === uhcProviderId)
        .reduce((count, val) => {
          const users = val?.users || [];
          return count + users.length;
        }, 0);
    }
    return 0;
  };

  const getExpiringCount = (requests) => {
    if (requests) {
      const clonedRequests = [...requests];
      if (!uhcProviderId) {
        return clonedRequests.reduce((count, val) => {
          const users = val?.users || [];
          const filteredRequests = users.filter((r) => r.requestExpiringDate);
          return count + filteredRequests.length;
        }, 0);
      }
      return clonedRequests
        .filter((r) => r.uhcProviderId === uhcProviderId)
        .reduce((count, val) => {
          const users = val?.users || [];
          const filteredRequests = users.filter((r) => r.requestExpiringDate);
          return count + filteredRequests.length;
        }, 0);
    }
    return 0;
  };

  const [, callDSOrganizationsApi] = useDSOrganizationsQuery({
    onCompleted: (result) => {
      if (result.orgList.length === 1) {
        setDSOrganization(result.orgList[0]);
      }
    },
    onError: () => null,
  });

  useEffect(() => {
    callDSOrganizationsApi({});
  }, []);

  useEffect(() => {
    setIsAccessBarLoading(
      userRequestsResultLoading && thirdPartyRequestsResultLoading
    );
  }, [userRequestsResult, thirdPartyRequestsResult]);

  const handlePendingUserRequestsClick = () => {
    router.navigate('/accessmanagement/usermanagement/pendingrequestlist/');
    event('ACCESS_REQUESTS_BAR_CLICK', {
      interactionContext: 'Pending User Requests',
    });
  };

  const handleExpiringUserRequestsClick = () => {
    router.navigate('/accessmanagement/usermanagement/expiringrequestlist/');
    event('ACCESS_REQUESTS_BAR_CLICK', {
      interactionContext: 'Expiring User Requests',
    });
  };

  const handlePendingThirdPartyRequestsClick = () => {
    router.navigate('/accessmanagement/thirdpartyaccess/pendingrequestlist/');
    event('ACCESS_REQUESTS_BAR_CLICK', {
      interactionContext: 'Pending 3rd Party Requests',
    });
  };

  const handleExpiringThirdPartyRequestsClick = () => {
    router.navigate('/accessmanagement/thirdpartyaccess/expiringrequestlist/');
    event('ACCESS_REQUESTS_BAR_CLICK', {
      interactionContext: 'Expiring 3rd Party Requests',
    });
  };

  const requiredVariant = (actionsCount) => {
    let variant;
    if (actionsCount > 45) {
      variant = 'error';
    } else if (actionsCount >= 1 && actionsCount <= 45) {
      variant = 'warning';
    } else {
      variant = 'success';
    }

    return variant;
  };

  const accessNavListItems = [
    {
      id: 'pending-user-requests',
      title: (
        <Layout.Stack alignLayout='right' space={-5}>
          <Text size='$sm' color='$black'>
            Pending user requests
          </Text>
        </Layout.Stack>
      ),
      actionText: getCount(userRequestsResult),
      variant: requiredVariant(getCount(userRequestsResult)),
      onClick: handlePendingUserRequestsClick,
    },
    {
      id: 'expiring-user-requests',
      title: (
        <Layout.Stack alignLayout='right' space={-5}>
          <Text size='$sm' color='$black'>
            Expiring user requests
          </Text>
        </Layout.Stack>
      ),
      actionText: getExpiringCount(userRequestsResult),
      variant: requiredVariant(getExpiringCount(userRequestsResult)),
      onClick: handleExpiringUserRequestsClick,
    },
    {
      id: 'pending-third-party-requests',
      title: (
        <Layout.Stack alignLayout='right' space={-5}>
          <Text size='$sm' color='$black'>
            Pending 3rd party requests
          </Text>
        </Layout.Stack>
      ),
      actionText: getCount(thirdPartyRequestsResult),
      variant: requiredVariant(getCount(thirdPartyRequestsResult)),
      onClick: handlePendingThirdPartyRequestsClick,
    },
    {
      id: 'expiring-third-party-requests',
      title: (
        <Layout.Stack alignLayout='right' space={-5}>
          <Text size='$sm' color='$black'>
            Expiring 3rd party requests
          </Text>
        </Layout.Stack>
      ),
      actionText: getExpiringCount(thirdPartyRequestsResult),
      variant: requiredVariant(getExpiringCount(thirdPartyRequestsResult)),
      onClick: handleExpiringThirdPartyRequestsClick,
    },
  ];

  const filteredAccessNavItems = () => {
    return !paa && ia
      ? accessNavListItems.filter(
          (item) =>
            item.id === 'pending-user-requests' ||
            item.id === 'expiring-user-requests'
        )
      : accessNavListItems;
  };

  useEffect(() => {
    setFilteredAccessBarItems(filteredAccessNavItems());
  }, [userRequestsResult, thirdPartyRequestsResult]);

  return (
    <>
      <Choose>
        <When condition={isAccessBarLoading}>
          <Skeleton height={20} />
        </When>
        <Otherwise>
          <ActionNav
            title={
              <Layout.Group>
                <Text size='20px' color='$primary1'>
                  Access Requests
                </Text>
              </Layout.Group>
            }
            headingLevel='h2'
          >
            <For each='accessNavItem' index='index' of={filteredAccessBarItems}>
              <StyledActionNavItem {...accessNavItem} />
            </For>
          </ActionNav>
        </Otherwise>
      </Choose>
    </>
  );
};
